'use client';
import clsx from 'clsx';
import { getPublishingImage } from '@/shared/utils/publishingAsset';
import { useEffect, useState, useRef } from 'react';
import { useMediaQuery } from '@publishing/hooks/useMediaQuery';
import { useMounted } from '@/shared/hooks/useMounted';

import Link from 'next/link';
import CountUp from 'react-countup';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper';
import type { Swiper as SwiperClass } from 'swiper/types';
import Icon from '@publishing/components/ui/Icon';
import { Button } from '@publishing/components/ui/Button';
import { CommonInner } from '@publishing/components/ui/CommonInner';
import { LEAD_FORM_NAME } from '@/features/lead-form/constants/formName';
import { LEAD_TYPE } from '@/features/lead-form/constants/leadType';
import { Form, FORM_KEY, openPopupMulti } from '@publishing/modules/forms';
import { FloatingConfig } from '@/shared/components/FloatingConfig';
import InlineDefault from '@publishing/modules/submit/InlineDefault';
import type { BlogArticle } from '@publishing/modules/submit/_types';
import { Input } from '@publishing/components/forms';
import { Title } from '@publishing/components/ui/Title';
import { EtcLeadform } from '@publishing/components/ui/SvgSprite';
import { MobilePopupBanner } from '@/features/banner/components/MobilePopupBanner';
import { ProductBanner } from '@/features/banner/components/ProductBanner';
import 'swiper/swiper-bundle.css';
import styles from './page.module.scss';

import IconPlay from '@publishing/assets/icons/main/btn_video_play.svg';
import IconPause from '@publishing/assets/icons/main/btn_video_stop.svg';

import { GTM_EVENT } from '@/shared/constants/gtm';
import { gtmClick } from '@/shared/utils/gtm';
const MAIN_VISUAL_PC_MP4 =
  'https://assets.fastfive.co.kr/video/home/visual_pc.mp4';
const MAIN_VISUAL_MO_MP4 =
  'https://assets.fastfive.co.kr/video/home/visual_mo.mp4';
const VIDEO_MQ = '(min-width: 1024px)';
const DEFAULT_INTERSECTION_OPTIONS: IntersectionObserverInit = {};

const STAT_MQ = '(min-width: 1024px)';
import {
  PRODUCT_GROUPS,
  type ProductGroup,
  type ProductItem,
  TIMELINE,
  STATS,
  formatStatNumber,
} from './data';

// 스크롤 에니메이션 트리거
export function useAnimationTrigger<T extends HTMLElement = HTMLElement>(
  options: IntersectionObserverInit = DEFAULT_INTERSECTION_OPTIONS,
) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect(); // 한 번만 실행
        }
      },
      { threshold: 0.2, ...options },
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [options]);

  return [ref, inView] as const;
}

// 비주얼 섹션 - 비디오
export function MainHeroSection() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoSrc, setVideoSrc] = useState<string>();
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentText, setCurrentText] = useState('');

  useEffect(() => {
    const mediaQuery = window.matchMedia(VIDEO_MQ);
    const updateVideoSrc = () => {
      setVideoSrc(
        mediaQuery.matches ? MAIN_VISUAL_PC_MP4 : MAIN_VISUAL_MO_MP4,
      );
    };

    updateVideoSrc();
    mediaQuery.addEventListener('change', updateVideoSrc);

    return () => mediaQuery.removeEventListener('change', updateVideoSrc);
  }, []);

  // 시간 감지
  const handleTimeUpdate = () => {
    const currentTime = videoRef.current?.currentTime ?? 0;
    const matched = TIMELINE.find(
      (item) => currentTime >= item.start && currentTime < item.end,
    );
    if (matched) setCurrentText(matched.text);
  };

  // 재생/멈춤
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
    setIsPlaying((v) => !v);
  };

  return (
    <section className={styles.hero_section_wrapper}>
      <div className={styles.hero_section_inner}>
        <h1>
          <p className={styles.main_title}>
            <span>사무실의 모든 것,</span>
          </p>
          <p className={styles.main_title}>
            <span>패스트파이브 하나로.</span>
          </p>
        </h1>
        <Button
          type="white"
          size="large"
          action={{
            openType: 'multi',
            // 태그 스펙: leadform_name=N_통합상담문의, item_category·item_category2 공란.
            gtmOpen: {
              formName: LEAD_FORM_NAME.INTEGRATED_CONSULT,
              leadFormNum: 1,
              leadType: [],
              productType: [],
            },
          }}
        >
          빠른 상담 받기
        </Button>
      </div>
      <video
        ref={videoRef}
        src={videoSrc}
        onTimeUpdate={handleTimeUpdate}
        autoPlay={isPlaying}
        muted
        loop
        playsInline
        disablePictureInPicture
        disableRemotePlayback
        className={styles.main_video}
      />

      <div className={styles.video_control_wrapper}>
        <button onClick={togglePlay} className={styles.play_btn}>
          {isPlaying ? (
            <IconPause width={32} height={32} aria-hidden />
          ) : (
            <IconPlay width={32} height={32} aria-hidden />
          )}
        </button>
        <p className={styles.description}>{currentText}</p>
      </div>
    </section>
  );
}

// 사업 카테고리 - 카드
function MainCardSolution({ iconName, href, text, title, target }: ProductItem) {
  return (
    <Link
      href={href}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      className={styles.card_solution_item}
      {...gtmClick(GTM_EVENT.BTN, title, href)}
    >
      <Icon
        name={iconName}
        theme="colored"
        size={[80, 56]}
        className={styles.card_solution_icon}
      />
      <p className={styles.card_solution_text}>{text}</p>
      <h3 className={styles.card_solution_title}>
        {title}
        <span className={styles.card_solution_more}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={16}
            height={16}
            fill="none"
            viewBox="0 0 16 16"
            aria-hidden
          >
            <path
              fill="currentColor"
              d="M7.294 3.023a1 1 0 0 1 1.414 0l4.27 4.27q.069.07.124.152 0 .002.003.005l.044.073.004.009a1 1 0 0 1 .098.273 1 1 0 0 1-.274.902l-4.269 4.27a1 1 0 1 1-1.414-1.415L9.856 9H3.731a1 1 0 1 1 0-2h6.125L7.294 4.437a1 1 0 0 1 0-1.414"
            />
          </svg>
        </span>
      </h3>
    </Link>
  );
}

// 사업 카테고리 - 리스트
const PRODUCT_CARD_STAGGER_MS = 90;

function ProductGroupBlock({ group }: { group: ProductGroup }) {
  const [ref, inView] = useAnimationTrigger<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={clsx(styles.product_group, inView && styles.__animate)}
    >
      <h3 className={styles.product_group_label}>{group.label}</h3>
      <ul className={styles.product_card_list}>
        {group.items.map((item, index) => (
          <li
            key={item.title}
            style={
              {
                '--product-card-delay': `${index * PRODUCT_CARD_STAGGER_MS}ms`,
              } as React.CSSProperties
            }
          >
            <MainCardSolution {...item} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ProductListSection() {
  return (
    <section className={styles.product_section}>
      <CommonInner>
        <Title level={2} name="headline" size="100" align="left">
          일하는 공간의
          <br />
          모든 고민을 해결합니다
        </Title>
        {PRODUCT_GROUPS.map((group) => (
          <ProductGroupBlock key={group.label} group={group} />
        ))}
      </CommonInner>
    </section>
  );
}

// 수치 슬롯 - 데스크탑 여부
function useIsStatDesktop() {
  return useMediaQuery(STAT_MQ, true);
}

// 수치 슬롯 - 슬라이더 - 레거시
function StatSection_Old() {
  const [ref, inView] = useAnimationTrigger();
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasSlideChanged, setHasSlideChanged] = useState(false);

  const mounted = useMounted();
  const isDesktop = useIsStatDesktop();

  const targetOffset = isDesktop ? 2 : 1;
  const targetIndex = (activeIndex + targetOffset) % STATS.length;

  const handleSlideChange = (swiper: SwiperClass) => {
    setActiveIndex(swiper.realIndex);
    setHasSlideChanged(true);
  };

  return (
    <section
      ref={ref}
      className={clsx(styles.stat_section, inView && styles.__animate)}
    >
      <CommonInner maxWidth={1020} align="center" className={styles.stat_section_inner}>
        <div className={styles.stat_left}>
          <p>국내 1위 오피스 브랜드</p>
          <p>숫자로 증명합니다</p>
        </div>

        {/* <Swiper
          slidesPerView={3}
          autoplay={{ delay: 2000, disableOnInteraction: false }}
          direction="vertical"
          breakpoints={{
            1024: { slidesPerView: 5, spaceBetween: 64 },
          }}
          loop={true}
          modules={[Autoplay]}
          className={styles.stat_list}
          spaceBetween={40}
          onSlideChange={handleSlideChange}
        >
          {STATS.map((item, index) => {
            const isTarget = index === targetIndex;
            const numValue = item.countValue;

            const decimals = Number.isInteger(item.countValue)
              ? 0
              : (String(item.countValue).split('.')[1]?.length ?? 0);

            // 최초 로드에서는 최종값 노출
            // 슬라이드가 변경된 이후부터 target에 CountUp 적용
            const shouldCountUp =
              mounted && hasSlideChanged && isTarget;

            return (
              <SwiperSlide key={item.label} className={styles.stat_item}>
                <span className={styles.stat_label}>{item.label}</span>

                <strong className={styles.stat_value}>
                  {shouldCountUp ? (
                    <CountUp
                      key={activeIndex}
                      start={item.startValue}
                      end={numValue}
                      duration={1.5}
                      decimals={decimals}
                      formattingFn={(value) => {
                        const num =
                          decimals > 0
                            ? value.toLocaleString('ko-KR', {
                                minimumFractionDigits: decimals,
                                maximumFractionDigits: decimals,
                              })
                            : Math.round(value).toLocaleString('ko-KR');

                        return `${num}${item.suffix}`;
                      }}
                    />
                  ) : (
                    `${formatStatNumber(item.countValue)}${item.suffix}`
                  )}
                </strong>
              </SwiperSlide>
            );
          })}
        </Swiper> */}
        <ul className={styles.stat_list}>
          {STATS.map((item) => (
            <li key={item.label} className={styles.stat_item}>
              <span className={styles.stat_label}>{item.label}</span>
              <strong className={styles.stat_value}>
                {formatStatNumber(item.countValue)}{item.suffix}
              </strong>
            </li>
          ))}
        </ul>
      </CommonInner>

      <picture className={styles.stat_graph}>
        <source
          srcSet={getPublishingImage('main/stat_graph_pc.png')}
          media="(min-width: 1024px)"
        />
        <source
          srcSet={getPublishingImage('main/stat_graph_mo.png')}
          media="(max-width: 1023px)"
        />
        <img
          src={getPublishingImage('main/stat_graph_pc.png')}
          alt="stat graph"
        />
      </picture>
    </section>
  );
}

// 수치 슬롯 섹션 - hydration 처리
export function StatSection() {
  const [ref, inView] = useAnimationTrigger();
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasSlideChanged, setHasSlideChanged] = useState(false);

  const mounted = useMounted();
  const isDesktop = useIsStatDesktop();

  const targetOffset = isDesktop ? 2 : 1;
  const targetIndex = (activeIndex + targetOffset) % STATS.length;

  const handleSlideChange = (swiper: SwiperClass) => {
    setActiveIndex(swiper.realIndex);
    setHasSlideChanged(true);
  };

  return (
    <section
      ref={ref}
      className={clsx(styles.stat_section, inView && styles.__animate)}
    >
      <CommonInner maxWidth={1020} align="center" className={styles.stat_section_inner}>
        <div className={styles.stat_left}>
          <p>국내 1위 오피스 브랜드</p>
          <p>숫자로 증명합니다</p>
        </div>

        {mounted ? (
          <Swiper
            slidesPerView={3}
            autoplay={{ delay: 2000, disableOnInteraction: false }}
            direction="vertical"
            breakpoints={{
              1024: { slidesPerView: 5, spaceBetween: 64 },
            }}
            loop={true}
            modules={[Autoplay]}
            className={styles.stat_list}
            spaceBetween={40}
            onSlideChange={handleSlideChange}
          >
            {STATS.map((item, index) => {
              const isTarget = index === targetIndex;
              const numValue = item.countValue;

              const decimals = Number.isInteger(item.countValue)
                ? 0
                : (String(item.countValue).split('.')[1]?.length ?? 0);

              const shouldCountUp = hasSlideChanged && isTarget;

              return (
                <SwiperSlide key={item.label} className={styles.stat_item}>
                  <span className={styles.stat_label}>{item.label}</span>

                  <strong className={styles.stat_value}>
                    {shouldCountUp ? (
                      <CountUp
                        key={activeIndex}
                        start={item.startValue}
                        end={numValue}
                        duration={1.5}
                        decimals={decimals}
                        formattingFn={(value) => {
                          const num =
                            decimals > 0
                              ? value.toLocaleString('ko-KR', {
                                  minimumFractionDigits: decimals,
                                  maximumFractionDigits: decimals,
                                })
                              : Math.round(value).toLocaleString('ko-KR');

                          return `${num}${item.suffix}`;
                        }}
                      />
                    ) : (
                      `${formatStatNumber(item.countValue)}${item.suffix}`
                    )}
                  </strong>
                </SwiperSlide>
              );
            })}
          </Swiper>
        ) : (
          <ul className={clsx(styles.stat_list, styles.stat_list_mgb)}>
            {STATS.map((item) => (
              <li key={item.label} className={styles.stat_item}>
                <span className={styles.stat_label}>{item.label}</span>
                <strong className={styles.stat_value}>
                  {formatStatNumber(item.countValue)}
                  {item.suffix}
                </strong>
              </li>
            ))}
          </ul>
        )}
      </CommonInner>

      <picture className={styles.stat_graph}>
        <source
          srcSet={getPublishingImage('main/stat_graph_pc.png')}
          media="(min-width: 1024px)"
        />
        <source
          srcSet={getPublishingImage('main/stat_graph_mo.png')}
          media="(max-width: 1023px)"
        />
        <img
          src={getPublishingImage('main/stat_graph_pc.png')}
          alt="stat graph"
        />
      </picture>
    </section>
  );
}


// 이벤트 허브 링크 섹션
export function EventBannerSection() {
  return (
    <div className={styles.event_banner}>
      <CommonInner align={['center', 'stretch']} className={styles.event_content}>
        <div className={styles.event_content_inner}>
          <Title align="left" className={styles.event_banner_title}>
            지금 가장 좋은 혜택을
            <br />
            확인해 보세요
          </Title>
          <Button
            type="white"
            size="large"
            href="/event/"
            className={styles.event_link}
          >
            진행 중인 이벤트 바로가기
          </Button>
        </div>
        <div className={styles.event_image} />
      </CommonInner>
    </div>
  );
}

export default function HomeContents({
  articles = [],
}: {
  articles?: BlogArticle[];
}) {
  const [formSubmitted, setFormSubmitted] = useState(false);

  return (
    <div className={styles.main_page}>
      <FloatingConfig
        lead={{
          consult: { action: () => openPopupMulti() },
          call: true,
        }}
        // scrollTop
        // zento={false}
      />

      {/* 비주얼 섹션 */}
      <MainHeroSection />
      <div data-header-toggle-zone />

      <ProductBanner bannerTypeCode="MAIN_PAGE_BANNER" />

      {/* 사업 카테고리 섹션 */}
      <ProductListSection />

      {/* 수치 슬롯 섹션 */}
      <StatSection />

      {/* 리드 폼 섹션 */}

      {formSubmitted ? (
        <InlineDefault articles={articles} />
      ) : (
        <CommonInner maxWidth={[708, 708]} align="center" className={styles.lead_form_inner}>
          <Title className={styles.lead_form_title}>오피스 고민 해결 <br />10초면 충분합니다</Title>
          <div className={styles.lead_form_wrap}>
              <Form
                type={FORM_KEY.SHARED_OFFICE}
                productType={[]} 
                formName={LEAD_FORM_NAME.INTEGRATED_CONSULT}
                leadFormNum={2}
                leadType={[LEAD_TYPE.SHARED_OFFICE]}
                // 태그 스펙: item_category·item_category2 공란 (leadType 은 SF 라우팅용이라 유지).
                gtmItemCategory={[]}
                gtmProductType={[]}
                submitLabel="무료 상담 받기"
                variant="main"
                onSuccess={() => setFormSubmitted(true)}
              />
          </div>
        </CommonInner>
      )}


      {/* 이벤트 허브 링크 섹션 */}
      <EventBannerSection />

      <MobilePopupBanner />

    </div>
  );
}
